//Utils
//Hooks
import React, { createContext, useContext } from 'react';

import clsx from 'clsx';

import useFormatKpiValue, { KPI_VALUE_CLASSIC } from '@/hooks/format/useFormatKpiValue';
import useFormatNumber, { percentFormatCompact } from '@/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import isNullOrUndefined from '@/utils/isNullOrUndefined';

import PropTypes from 'prop-types';

import Icon from '@/components/base/Icon';
//Components
import Tooltip from '@/components/base/Tooltip';

const ComparativeTableContext = createContext();

const useComparativeTableContext = () => {
    const context = useContext(ComparativeTableContext);
    if (!context) {
        throw new Error(
            `ComparativeTable compound components cannot be rendered outside the ComparativeTable component`
        );
    }
    return context;
};

const ComparativeTable = ({
    children,
    userNumbersFormat,
    defaultNs = 'analytics',
    className,
    tableConfig,
    ...others
}) => {
    const { t } = useTranslation(defaultNs);
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatKpiValue } = useFormatKpiValue(
        KPI_VALUE_CLASSIC,
        userNumbersFormat?.number,
        userNumbersFormat?.date?.format,
        userNumbersFormat?.time?.format
    );

    return (
        <ComparativeTableContext.Provider
            value={{ ...others, t, formatNumber, formatKpiValue, tableConfig }}>
            <div className={clsx('scrollbar flex w-full flex-col overflow-auto pb-2', className)}>
                <table className={clsx('w-full overflow-auto', className)}>{children}</table>
            </div>
        </ComparativeTableContext.Provider>
    );
};

ComparativeTable.propTypes = {
    children: PropTypes.node,
    userNumbersFormat: PropTypes.object.isRequired,
    defaultNs: PropTypes.string,
    className: PropTypes.string
};

const ComparativeTableHeader = ({ children }) => {
    return (
        <thead className="sticky top-0 z-10 border-b border-icoGray-300 bg-white dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
            <tr className="flex flex-nowrap">{children}</tr>
        </thead>
    );
};
ComparativeTableHeader.propTypes = {
    children: PropTypes.node
};

ComparativeTable.Header = ComparativeTableHeader;

const ComparativeTableHeaderTitle = () => {
    const { headerWidth, isSticky, tableConfig, t } = useComparativeTableContext();

    return (
        <th
            className={clsx(
                'flex w-[11.25rem] items-end bg-white font-GSemiBold text-9 uppercase text-icoGray-500 dark:bg-icoDarkMode-anthracite',
                headerWidth,
                {
                    'sticky left-0 z-1 max-w-[11.25rem]': isSticky
                }
            )}>
            {tableConfig?.title_head ? (
                <div className="rounded-tr-md bg-icoGray-100 px-5 py-3 dark:bg-icoDarkMode-deepBlack">
                    {t(tableConfig.title_head)}
                </div>
            ) : null}
        </th>
    );
};

ComparativeTable.HeaderTitle = ComparativeTableHeaderTitle;

const ComparativeTableHeaderItem = ({ className, ...restProps }) => {
    const { colWidth } = useComparativeTableContext();

    return <th className={clsx('text-xs font-medium', colWidth, className)} {...restProps} />;
};

ComparativeTableHeader.propTypes = {
    className: PropTypes.string
};

ComparativeTable.HeaderItem = ComparativeTableHeaderItem;

const ComparativeTableBody = (props) => {
    const { hasFixWidth } = useComparativeTableContext();

    return (
        <tbody
            className={clsx(
                'inline-block divide-y divide-icoGray-300 dark:divide-icoDarkMode-beton',
                {
                    'w-full': !hasFixWidth
                }
            )}
            {...props}
        />
    );
};
ComparativeTableBody.propTypes = {
    children: PropTypes.node
};

ComparativeTable.Body = ComparativeTableBody;

const getRowTitle = (row, t) => {
    if (row.title) {
        if (typeof row.title === 'string') {
            return t(row.title);
        } else {
            return <row.title />;
        }
    } else {
        return row.name;
    }
};

const ComparativeTableRow = ({ row, children }) => {
    const { headerWidth, isSticky, t } = useComparativeTableContext();

    return (
        <tr className="flex flex-nowrap">
            {row.type === 'header' ? (
                <td
                    className={clsx(
                        'flex items-end bg-white pt-5 font-GSemiBold text-9 uppercase text-icoGray-500 dark:bg-icoDarkMode-anthracite',
                        headerWidth,
                        { 'sticky left-0 z-1 max-w-[11.25rem] !flex-none': isSticky }
                    )}>
                    <div className="rounded-tr-md bg-icoGray-100 px-5 py-3 dark:bg-icoDarkMode-deepBlack">
                        {getRowTitle(row, t)}
                    </div>
                </td>
            ) : (
                <td
                    className={clsx(
                        'flex items-center justify-start border-r border-icoGray-300 bg-white p-5 px-5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite',
                        headerWidth,
                        { 'sticky left-0 z-2 max-w-[11.25rem]': isSticky }
                    )}>
                    <div className="font-GSemiBold text-xs">{getRowTitle(row, t)}</div>
                    {row?.tooltip ? (
                        /* Add span to prevent tippy warning */
                        <span className="relative ml-2">
                            <Tooltip
                                content={
                                    typeof row.tooltip === 'string' ? t(row.tooltip) : row.tooltip
                                }
                                placement="top"
                                popperOptions={{
                                    //prevent tooltip to pop in the wrong place
                                    strategy: 'fixed'
                                }}>
                                <Icon
                                    aria-label={t('commons:infos')}
                                    icon="help"
                                    className="text-xs text-icoGray-400 hover:text-icoGray-500 dark:text-icoDarkMode-wolf"
                                />
                            </Tooltip>
                        </span>
                    ) : null}
                </td>
            )}
            {children}
        </tr>
    );
};
ComparativeTableRow.propTypes = {
    row: PropTypes.shape({
        name: PropTypes.string,
        title: PropTypes.any,
        tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        type: PropTypes.oneOf(['header', PropTypes.string])
    }),
    children: PropTypes.node
};

ComparativeTable.Row = ComparativeTableRow;

// A bit complex but necessary because row[config.value_id] and row[config.value_id].value can both be null
// and null is an 'object' in javascript ...
const getCellData = (colData, rowConfig) => {
    let value = null;
    let evolution = null;

    if (isNullOrUndefined(colData[rowConfig.value_id])) return { value, evolution };

    if (colData[rowConfig.value_id].value !== undefined) {
        value = colData[rowConfig.value_id].value;
    } else {
        value = colData[rowConfig.value_id];
    }

    evolution = colData[rowConfig.value_id].evolution ?? null;

    return { value, evolution };
};

const Evolution = ({ className, evolution, row, formatNumber }) => (
    <span
        className={clsx('inline-flex items-center text-xs', className, {
            'text-icoRed':
                (evolution < 0 && !row?.evolution_reverse) ||
                (evolution > 0 && row?.evolution_reverse),
            'text-icoGreen':
                (evolution > 0 && !row?.evolution_reverse) ||
                (evolution < 0 && row?.evolution_reverse),
            'text-icoGray-400': evolution === 0
        })}>
        <Icon
            className="mr-1"
            icon={
                evolution > 0 ? 'arrow-45-top' : evolution < 0 ? 'arrow-45-bottom' : 'arrow-right'
            }
        />
        {evolution !== 0
            ? formatNumber(evolution, {
                  ...percentFormatCompact,
                  signDisplay: 'always'
              })
            : formatNumber(evolution, {
                  ...percentFormatCompact,
                  signDisplay: 'always'
              }).replace('+', '=')}
    </span>
);

const ComparativeTableRowItem = ({ row, col }) => {
    const { colWidth, formatKpiValue, formatNumber } = useComparativeTableContext();

    if (row.type === 'header') return null;

    const { value, evolution } = getCellData(col, row);

    return (
        <td
            className={clsx(
                'relative flex items-center justify-center border-r border-icoGray-300 bg-white p-5 font-GMedium text-xs last:border-r-0 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite',
                colWidth,
                {
                    'sticky left-[180px] z-1': col?.isFirstCol,
                    'z-0': !col?.isFirstCol
                }
            )}>
            {value || value === 0 ? formatKpiValue(value, row?.data_format) : 'N/A'}
            {evolution !== null && (
                <Evolution
                    className="absolute right-5"
                    evolution={evolution}
                    row={row}
                    formatNumber={formatNumber}
                />
            )}
        </td>
    );
};
ComparativeTableRowItem.propTypes = {
    row: PropTypes.object,
    col: PropTypes.object
};

ComparativeTable.RowItem = ComparativeTableRowItem;

export default ComparativeTable;
